import type { ItinerarySuccess } from "./api-types";
import type { PlannerLift } from "./data";
import type { AdaptivePlanContext, AdaptiveRequest } from "./adaptive-types";
import { haversineKm } from "@/lib/conditions/haversine";
import { validTrailVisits } from "./visit-history";

export function journeyDate(now: Date, timezone: string): string {
  return new Intl.DateTimeFormat("en-CA", { timeZone: timezone, year: "numeric", month: "2-digit", day: "2-digit" }).format(now);
}

export function journeyCheckpoint(plan: { itinerary: Pick<ItinerarySuccess["itinerary"], "legs"> }, index: number): string | null {
  if (!Number.isInteger(index) || index < 0 || index > plan.itinerary.legs.length) return null;
  const leg = plan.itinerary.legs[index];
  if (leg) return leg.kind === "stop" ? leg.nodeId : leg.fromNode ?? null;
  const last = plan.itinerary.legs.at(-1);
  return last ? last.kind === "stop" ? last.nodeId : last.toNode ?? null : null;
}

export function compactAdaptiveRequest(request: AdaptiveRequest): AdaptiveRequest {
  return { ...request, source: { ...request.source, legs: request.source.legs.map((leg) => leg.kind === "stop"
    ? { kind: "stop", nodeId: leg.nodeId, stopType: leg.stopType, placeId: null, label: leg.label, departHour: leg.departHour, arriveHour: leg.arriveHour }
    : { kind: leg.kind, refId: leg.refId, fromNode: leg.fromNode, toNode: leg.toNode, climb: leg.climb, label: leg.label, departHour: leg.departHour, arriveHour: leg.arriveHour }) } };
}

export function validAdaptiveContext(raw: unknown): raw is AdaptivePlanContext {
  if (!raw || typeof raw !== "object") return false;
  const value = raw as AdaptivePlanContext;
  if (value.guidanceOrigin !== undefined && (!value.guidanceOrigin || typeof value.guidanceOrigin.id !== "string" || !/^guidance-start-[0-9]+$/.test(value.guidanceOrigin.id)
    || !Number.isFinite(value.guidanceOrigin.lat) || Math.abs(value.guidanceOrigin.lat) > 90 || !Number.isFinite(value.guidanceOrigin.lng) || Math.abs(value.guidanceOrigin.lng) > 180)) return false;
  if (value.visitedTrails !== undefined && !validTrailVisits(value.visitedTrails)) return false;
  const positive = (n: unknown, max: number) => typeof n === "number" && Number.isFinite(n) && n >= 0 && n <= max;
  return ["one-more-run", "easier", "get-back", "shorter", "closed-lift", "refresh"].includes(value.action)
    && typeof value.assessedAt === "string" && Number.isFinite(Date.parse(value.assessedAt)) && typeof value.sourceDate === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value.sourceDate)
    && positive(value.deadlineHour, 24) && !!value.completed && !!value.budget
    && positive(value.completed.ascentM, 20_000) && positive(value.completed.pushes, 400) && Number.isInteger(value.completed.pushes) && positive(value.completed.walkingMinutes, 1440)
    && positive(value.budget.climbM, 20_000) && (value.budget.pushes === null || positive(value.budget.pushes, 400) && Number.isInteger(value.budget.pushes)) && positive(value.budget.walkingMinutes, 30);
}

export function journeyChanges(plan: ItinerarySuccess, checkpoint: number, currentHour: number, lifts: readonly PlannerLift[]) {
  const remaining = plan.itinerary.legs.slice(checkpoint);
  const start = remaining[0]?.departHour ?? currentHour;
  const finish = remaining.at(-1)?.arriveHour ?? currentHour;
  const requestedEnd = plan.preferences?.endTime.split(":").map(Number);
  const deadline = plan.adaptiveContext?.deadlineHour ?? (requestedEnd ? requestedEnd[0] + requestedEnd[1] / 60 : finish);
  const estimatedFinishHour = currentHour + Math.max(0, finish - start);
  const ids = new Set(remaining.flatMap((leg) => leg.kind === "lift" && leg.refId ? [leg.refId] : []));
  return {
    remainingMinutes: Math.max(0, Math.floor((deadline - currentHour) * 60)),
    estimatedFinishHour,
    lateMinutes: Math.max(0, Math.ceil((estimatedFinishHour - deadline) * 60)),
    blockedLifts: lifts.filter((lift) => ids.has(lift.id) && (lift.status === "closed" || lift.status === "hold")),
  };
}

/** Suggest a signed checkpoint only. Never mark progress from a single fix. */
export function nearbyJourneyCheckpoint(plan: ItinerarySuccess, fix: { lat: number; lng: number; accuracy: number; timestamp: number }, nowMs: number): { index: number; distanceM: number } | null {
  if (![fix.lat, fix.lng, fix.accuracy, fix.timestamp].every(Number.isFinite) || Math.abs(fix.lat) > 90 || Math.abs(fix.lng) > 180 || fix.accuracy < 0 || fix.accuracy > 25 || nowMs - fix.timestamp > 30_000 || fix.timestamp > nowMs + 5000) return null;
  const candidates: { index: number; node: string; distanceM: number }[] = [];
  for (let index = 0; index <= plan.itinerary.legs.length; index++) {
    const node = journeyCheckpoint(plan, index);
    const geometry = index === plan.itinerary.legs.length ? plan.route.at(-1)?.geometry : plan.route[index]?.geometry;
    const point = index === plan.itinerary.legs.length ? geometry?.coordinates.at(-1) : geometry?.coordinates[0];
    if (!node || !point) continue;
    const distanceM = haversineKm(fix.lat, fix.lng, point[1], point[0]) * 1000;
    if (distanceM <= 45) candidates.push({ index, node, distanceM });
  }
  candidates.sort((a, b) => a.distanceM - b.distanceM);
  const first = candidates[0];
  // Repeated laps at the same node cannot establish which lap has been ridden.
  if (!first || candidates.some((candidate) => candidate.index !== first.index && (candidate.node === first.node || candidate.distanceM - first.distanceM < Math.max(30, fix.accuracy * 2)))) return null;
  return { index: first.index, distanceM: Math.round(first.distanceM) };
}
